import { compareStringsUtf16 } from 'src/adapters/storeToPageURL/builders/compareStringsUtf16';
import { buildTaxonomyHref } from 'src/adapters/storeToPageURL/facets/buildTaxonomyHref';
import { getTaxonomyNavLevelOptions } from 'src/redux/productsQuery/helpers/getTaxonomyNavLevelOptions';
import type { TaxonomyContext } from 'src/redux/productsQuery/selectors/selectTaxonomyContext';
import type { Facet, FacetOption } from 'src/redux/productsQuery/types/Facet';
import type { TaxonomyAttribute } from 'src/redux/productsQuery/types/ProductsQueryState';

export interface TaxonomyNavLink {
  active: boolean;
  crawlable: boolean;
  href: string;
  text: string;
  value: string;
}

/** Shapes pre-resolved visible options of a taxonomy level into nav links. */
const buildNavItems = ({
  facets,
  isFollowable,
  levelIndex,
  options,
  surface,
  taxonomyAttributeAliases,
}: {
  facets: Facet[];
  isFollowable: (href: string) => boolean;
  levelIndex: number;
  options: FacetOption[];
  surface?: string;
  taxonomyAttributeAliases: TaxonomyAttribute[];
}): TaxonomyNavLink[] => {
  if (options.length === 0) {
    return [];
  }

  const levelAlias = taxonomyAttributeAliases[levelIndex]!;

  // First entry is always "All": it clears any selection at this level (keeping
  // the ancestors) and is the active entry whenever nothing at this level is
  // selected. At the deepest level a sibling is selected, so "All" becomes a
  // link back to the parent level instead.
  const hasActiveOption = options.some((option) => option.active);
  const allHref = buildTaxonomyHref({
    facets,
    levelCount: levelIndex,
    surface,
    taxonomyAttributeAliases,
  });
  const allItem: TaxonomyNavLink = {
    active: !hasActiveOption,
    // "All" follows the same rule as every other link: crawlable only when its
    // taxonomy href is an exact indexable entry in the neighbourhood map.
    crawlable: isFollowable(allHref),
    href: allHref,
    text: 'All',
    // `value` is only a React key / test-id. `'all'` is safe as the synthetic
    // entry's value because it cannot collide with a real option value — it is
    // the reserved "skip this level" segment and so never round-trips from a URL
    // into a facet option.
    value: 'all',
  };

  // A link is crawlable iff its taxonomy href exactly matches an indexable
  // neighbourhood-map entry. `text` is the raw option title; character-casing is
  // applied by the rendering component.
  const optionItems: TaxonomyNavLink[] = options.map((option) => {
    const href = buildTaxonomyHref({
      facets,
      leaf: { alias: levelAlias, values: [option.value] },
      levelCount: levelIndex,
      surface,
      taxonomyAttributeAliases,
    });

    return {
      active: option.active,
      crawlable: isFollowable(href),
      href,
      text: option.title,
      value: option.value,
    };
  });

  // The nav always lists options in case-insensitive alphabetical order
  // (by displayed title), independent of the store/config order the options
  // arrive in. Lowercasing before the code-unit comparison keeps the order
  // "alphabetical as displayed" regardless of source casing (the renderer
  // sentence-cases labels, which never changes their relative order). "All" is
  // excepted: it is always pinned first below.
  const sortedOptionItems = [...optionItems].sort((a, b) =>
    compareStringsUtf16(a.text.toLowerCase(), b.text.toLowerCase()),
  );

  return [allItem, ...sortedOptionItems];
};

/**
 * Decides which level the nav should render and with which options: the children
 * to drill into (already resolved on the context) when there are any, otherwise
 * the current level's siblings (none at depth 0).
 */
const resolveNavLevel = (
  context: TaxonomyContext,
): { levelIndex: number; options: FacetOption[] } | null => {
  const { depth, facets, isDeepestLevel, taxonomyAttributeAliases } = context;

  if (!isDeepestLevel) {
    return {
      levelIndex: depth,
      options: getTaxonomyNavLevelOptions(facets, taxonomyAttributeAliases, depth),
    };
  }

  if (depth === 0) {
    return null;
  }

  const options = getTaxonomyNavLevelOptions(facets, taxonomyAttributeAliases, depth - 1);

  return options.length > 0 ? { levelIndex: depth - 1, options } : null;
};

/**
 * Builds the taxonomy navigation view-model (the "All" aggregate plus one link
 * per visible option) from the taxonomy context, or an empty array when the page is
 * unsupported or has no level to navigate. `isFollowable` is the route's
 * followability predicate ({@link selectIsHrefFollowable}) that decides each
 * link's crawlability; it returns false for every href off the taxonomy route or
 * when the neighbourhood map is empty.
 */
export const adaptTaxonomyContextToNavLinks = (
  context: TaxonomyContext,
  isFollowable: (href: string) => boolean = () => false,
): TaxonomyNavLink[] => {
  if (context.unsupported) {
    return [];
  }

  const navLevel = resolveNavLevel(context);
  if (!navLevel) {
    return [];
  }

  return buildNavItems({
    facets: context.facets,
    isFollowable,
    surface: context.surface,
    taxonomyAttributeAliases: context.taxonomyAttributeAliases,
    ...navLevel,
  });
};
